import { supabase } from "@/integrations/supabase/client";

export type AttributeType = 'pot_size' | 'pot_type' | 'pot_color';

export interface AttributeValue {
  id: string; // value_id like 'small-4'
  name: string;
  enabled: boolean;
  metadata?: any;
  position?: number;
}

export interface Attribute {
  type: AttributeType;
  label: string;
  values: AttributeValue[];
}

const DEFAULT_LABELS: Record<AttributeType, string> = {
  pot_size: 'Pot Size',
  pot_type: 'Pot Type',
  pot_color: 'Pot Color',
};

export const productAttributeService = {
  async fetchAttributes(productId: string): Promise<Attribute[]> {
    // 1) load attribute rows
    const { data: attrs, error: attrErr } = await supabase
      .from('product_attributes')
      .select('id, name, label')
      .eq('product_id', productId)
      .order('name', { ascending: true });

    if (attrErr) {
      console.error('Error loading attributes:', attrErr);
      throw new Error(attrErr.message);
    }

    // Ensure base attributes exist in memory even if not created yet
    const attrByName = new Map<string, { id?: string; name: string; label: string }>();
    (['pot_size','pot_type','pot_color'] as AttributeType[]).forEach((n) => {
      const row = attrs?.find(a => a.name === n);
      attrByName.set(n, {
        id: row?.id,
        name: n,
        label: row?.label || DEFAULT_LABELS[n],
      });
    });

    // 2) load values in a single query by product
    const { data: values, error: valErr } = await supabase
      .from('attribute_values')
      .select('id, attribute_id, value_id, name, enabled, position, metadata')
      .eq('product_id', productId)
      .order('position', { ascending: true });

    if (valErr) {
      console.error('Error loading values:', valErr);
      throw new Error(valErr.message);
    }

    // group values by attribute_id
    const valuesByAttr = new Map<string, AttributeValue[]>();
    (values || []).forEach(v => {
      if (!v.attribute_id) return;
      const list = valuesByAttr.get(v.attribute_id) || [];
      list.push({ id: v.value_id, name: v.name, enabled: !!v.enabled, metadata: v.metadata || {}, position: v.position ?? 0 });
      valuesByAttr.set(v.attribute_id, list);
    });

    // Default values to use when attributes have no values in DB
    const DEFAULT_VALUES: Record<AttributeType, AttributeValue[]> = {
      pot_size: [
        { id: 'small-4', name: 'Small (4")', enabled: true },
        { id: 'medium-5', name: 'Medium (5")', enabled: true },
        { id: 'large-8', name: 'Large (8")', enabled: true },
        { id: 'xlarge-10', name: 'X-Large (10")', enabled: true },
      ],
      pot_type: [
        { id: 'nursery-pot', name: 'Nursery Pot', enabled: true },
        { id: 'cool-pot', name: 'Cool Pot', enabled: true },
        { id: 'krish', name: 'Krish', enabled: true },
        { id: 'round-self-watering', name: 'Round Self Watering', enabled: true },
        { id: 'square-self-watering', name: 'Square Self Watering', enabled: true },
      ],
      pot_color: [
        { id: 'white', name: 'White', enabled: true, metadata: { hexColor: '#ffffff' } },
        { id: 'yellow', name: 'Yellow', enabled: true, metadata: { hexColor: '#ffd700' } },
        { id: 'orange', name: 'Orange', enabled: true, metadata: { hexColor: '#ff8c00' } },
        { id: 'red', name: 'Red', enabled: true, metadata: { hexColor: '#dc2626' } },
        { id: 'black', name: 'Black', enabled: true, metadata: { hexColor: '#000000' } },
        { id: 'beige', name: 'Beige', enabled: true, metadata: { hexColor: '#f5f5dc' } },
      ],
    };

    // Build final attributes array, using defaults for attributes with no values
    const result: Attribute[] = (['pot_size','pot_type','pot_color'] as AttributeType[]).map((name) => {
      const meta = attrByName.get(name)!;
      const attrValues = meta.id ? (valuesByAttr.get(meta.id) || []) : [];
      
      // If this attribute has no values in DB, use defaults
      const finalValues = attrValues.length > 0 ? attrValues : DEFAULT_VALUES[name];
      
      return { type: name, label: meta.label, values: finalValues };
    });

    return result;
  },

  async saveAttributes(productId: string, attributes: Attribute[]): Promise<void> {
    // Validate inputs
    if (!productId || productId === 'temp') {
      throw new Error('Cannot save attributes for unsaved product');
    }
    
    if (!attributes || attributes.length === 0) {
      throw new Error('No attributes to save');
    }

    // Retry logic with exponential backoff
    let lastError: Error | null = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await this._saveAttributesInternal(productId, attributes);
        return; // Success, exit
      } catch (error) {
        lastError = error as Error;
        if (attempt < 2) {
          // Wait before retrying (exponential backoff: 500ms, 1000ms)
          await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, attempt)));
        }
      }
    }
    
    // All retries failed
    throw lastError || new Error('Failed to save attributes after multiple attempts');
  },

  async _saveAttributesInternal(productId: string, attributes: Attribute[]): Promise<void> {
    // 1) Load current attributes to get their ids (avoid ON CONFLICT requirement)
    const { data: existingAttrs, error: readErr } = await supabase
      .from('product_attributes')
      .select('id, name, label')
      .eq('product_id', productId);
    if (readErr) throw new Error(readErr.message);

    const idByName = new Map<string, string>();

    // 2) Ensure each attribute row exists (insert or update), capture ids
    for (const a of attributes) {
      const current = existingAttrs?.find((e) => e.name === a.type);
      if (current) {
        // Update label if changed
        if ((current.label || '') !== (a.label || DEFAULT_LABELS[a.type])) {
          const { error: updErr } = await supabase
            .from('product_attributes')
            .update({ label: a.label || DEFAULT_LABELS[a.type] })
            .eq('id', current.id);
          if (updErr) throw new Error(updErr.message);
        }
        idByName.set(a.type, current.id);
      } else {
        const { data: inserted, error: insErr } = await supabase
          .from('product_attributes')
          .insert({ product_id: productId, name: a.type, label: a.label || DEFAULT_LABELS[a.type] })
          .select('id')
          .single();
        if (insErr) throw new Error(insErr.message);
        idByName.set(a.type, inserted!.id);
      }
    }

    // 3) Replace attribute values per attribute deterministically
    for (const attr of attributes) {
      const attribute_id = idByName.get(attr.type);
      if (!attribute_id) continue;

      // Delete all previous values for this attribute, then insert the current list
      const { error: delValsErr } = await supabase
        .from('attribute_values')
        .delete()
        .eq('attribute_id', attribute_id)
        .eq('product_id', productId);
      if (delValsErr) throw new Error(delValsErr.message);

      if (attr.values.length === 0) continue;

      const rows = attr.values.map((v, idx) => ({
        product_id: productId,
        attribute_id,
        value_id: v.id,
        name: v.name,
        enabled: v.enabled,
        position: idx,
        metadata: v.metadata || {},
      }));

      const { error: insertValsErr } = await supabase
        .from('attribute_values')
        .insert(rows);
      if (insertValsErr) throw new Error(insertValsErr.message);
    }
  }
};
